if (!customElements.get('delivery-date-picker')) {
  customElements.define('delivery-date-picker', class DeliveryDatePicker extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.months = (this.dataset.months || '').split('|');
      this.weekdays = (this.dataset.weekdays || '').split('|');
      this.locale = this.dataset.locale || 'es-ES';
      this.dateInput = this.querySelector('[data-delivery-date-input]');
      this.slotInput = this.querySelector('[data-delivery-slot-input]');
      this.instructions = this.querySelector('[data-delivery-instructions]');
      this.dateError = this.querySelector('[data-date-error]');
      this.slotError = this.querySelector('[data-slot-error]');
      this.status = this.querySelector('[data-delivery-status]');
      this.days = this.querySelector('[data-calendar-days]');
      this.monthLabel = this.querySelector('[data-calendar-month]');
      this.prev = this.querySelector('[data-calendar-prev]');
      this.next = this.querySelector('[data-calendar-next]');
      this.slotOptions = this.querySelector('[data-slot-options]');
      this.closedDates = new Set((this.dataset.closedDates || '').split(/[\s,;|]+/).filter(Boolean));
      this.manualOpenDates = new Set((this.dataset.manualOpenDates || '').split(/[\s,;|]+/).filter(Boolean));
      this.manualOpenSlot = this.dataset.manualOpenSlot || 'all';
      this.saturdayAfternoonEnabled = this.dataset.saturdayAfternoonEnabled === 'true';
      this.instructions.value = this.dataset.savedInstructions || '';
      this.instructions.addEventListener('input', () => {
        clearTimeout(this.instructionsTimer);
        this.instructionsTimer = setTimeout(() => {
          this.broadcast();
          this.saveAttributes();
        }, 550);
      });

      const now = this.madridNow();
      this.today = this.makeDate(now.year, now.month, now.day);
      this.automaticMinimum = this.earliestDelivery(now);
      this.minimum = this.today;
      this.maximum = this.addDays(this.today, 90);

      const savedDate = this.parseDate(this.dataset.savedDate);
      this.selected = savedDate && this.isAvailable(savedDate) ? savedDate : null;
      this.selectedSlot = this.dataset.savedSlot || '';
      if (!this.slotValues(this.selected).includes(this.selectedSlot)) this.selectedSlot = '';

      this.visible = this.selected
        ? this.makeDate(this.selected.getUTCFullYear(), this.selected.getUTCMonth() + 1, 1)
        : this.makeDate(this.minimum.getUTCFullYear(), this.minimum.getUTCMonth() + 1, 1);

      this.querySelector('[data-calendar-weekdays]').innerHTML = this.weekdays.map(function(day) {
        return '<span>' + day + '</span>';
      }).join('');

      this.prev.addEventListener('click', () => this.moveMonth(-1));
      this.next.addEventListener('click', () => this.moveMonth(1));

      this.checkoutButton = document.getElementById(this.dataset.checkoutId);
      this.checkoutForm = document.getElementById(this.dataset.formId);
      this.validateBound = (event) => this.validateCheckout(event);
      if (this.checkoutButton) this.checkoutButton.addEventListener('click', this.validateBound, true);
      if (this.checkoutForm) this.checkoutForm.addEventListener('submit', this.validateBound, true);

      this.syncBound = (event) => {
        if (event.detail.source === this) return;
        this.selected = this.parseDate(event.detail.date);
        this.selectedSlot = event.detail.slot || '';
        if (typeof event.detail.instructions === 'string') this.instructions.value = event.detail.instructions;
        this.updateInputs();
        this.render();
      };
      document.addEventListener('fm:delivery-updated', this.syncBound);

      this.updateInputs();
      this.render();
    }

    disconnectedCallback() {
      if (this.checkoutButton && this.validateBound) this.checkoutButton.removeEventListener('click', this.validateBound, true);
      if (this.checkoutForm && this.validateBound) this.checkoutForm.removeEventListener('submit', this.validateBound, true);
      if (this.syncBound) document.removeEventListener('fm:delivery-updated', this.syncBound);
    }

    madridNow() {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Madrid',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23'
      }).formatToParts(new Date());
      const values = {};
      parts.forEach(function(part) {
        if (part.type !== 'literal') values[part.type] = Number(part.value);
      });
      return values;
    }

    makeDate(year, month, day) {
      return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    }

    addDays(date, amount) {
      const copy = new Date(date.getTime());
      copy.setUTCDate(copy.getUTCDate() + amount);
      return copy;
    }

    earliestDelivery(now) {
      const today = this.makeDate(now.year, now.month, now.day);
      const weekday = today.getUTCDay();
      const minutes = now.hour * 60 + now.minute;
      let offset;

      if (weekday === 1) {
        offset = minutes < 990 ? 1 : 2;
      } else if (weekday === 6) {
        offset = minutes < 720 ? 1 : 3;
      } else if (weekday === 0) {
        offset = 2;
      } else {
        offset = minutes < 990 ? 1 : 2;
      }

      let earliest = this.addDays(today, offset);
      while (earliest.getUTCDay() === 1) earliest = this.addDays(earliest, 1);
      return earliest;
    }

    parseDate(value) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
      const parts = value.split('-').map(Number);
      return this.makeDate(parts[0], parts[1], parts[2]);
    }

    formatDate(date) {
      if (!date) return '';
      const pad = function(value) { return String(value).padStart(2, '0'); };
      return date.getUTCFullYear() + '-' + pad(date.getUTCMonth() + 1) + '-' + pad(date.getUTCDate());
    }

    isAvailable(date) {
      if (!date) return false;
      const key = this.formatDate(date);
      if (date < this.today || date > this.maximum) return false;
      if (this.manualOpenDates.has(key)) return true;
      return date >= this.automaticMinimum &&
        date.getUTCDay() !== 1 &&
        !this.closedDates.has(key);
    }

    slotValues(date) {
      if (!date) return [];
      const key = this.formatDate(date);
      if (this.manualOpenDates.has(key)) {
        if (this.manualOpenSlot === 'morning') return [this.dataset.morning];
        if (this.manualOpenSlot === 'afternoon') return [this.dataset.afternoon];
      }
      if (date.getUTCDay() === 0) return [this.dataset.sunday];
      if (date.getUTCDay() === 6 && !this.saturdayAfternoonEnabled) return [this.dataset.morning];
      return [this.dataset.morning, this.dataset.afternoon];
    }

    moveMonth(amount) {
      this.visible = this.makeDate(
        this.visible.getUTCFullYear(),
        this.visible.getUTCMonth() + 1 + amount,
        1
      );
      this.renderCalendar();
    }

    render() {
      this.renderCalendar();
      this.renderSlots();
    }

    renderCalendar() {
      const year = this.visible.getUTCFullYear();
      const month = this.visible.getUTCMonth();
      this.monthLabel.textContent = this.months[month] + ' ' + year;

      const firstWeekday = (this.makeDate(year, month + 1, 1).getUTCDay() + 6) % 7;
      const totalDays = new Date(Date.UTC(year, month + 1, 0, 12)).getUTCDate();
      const cells = [];

      for (let i = 0; i < firstWeekday; i += 1) {
        cells.push('<span class="fm-calendar__day is-empty"></span>');
      }

      for (let day = 1; day <= totalDays; day += 1) {
        const date = this.makeDate(year, month + 1, day);
        const value = this.formatDate(date);
        const disabled = !this.isAvailable(date);
        const selected = this.selected && value === this.formatDate(this.selected);
        cells.push(
          '<button class="fm-calendar__day' + (selected ? ' is-selected' : '') +
          '" type="button" data-date="' + value + '"' +
          (disabled ? ' disabled' : '') +
          ' aria-pressed="' + selected + '">' + day + '</button>'
        );
      }

      this.days.innerHTML = cells.join('');
      this.days.querySelectorAll('[data-date]:not(:disabled)').forEach((button) => {
        button.addEventListener('click', () => this.selectDate(button.dataset.date));
      });

      const firstVisible = this.makeDate(year, month + 1, 1);
      const minimumMonth = this.makeDate(this.minimum.getUTCFullYear(), this.minimum.getUTCMonth() + 1, 1);
      const maximumMonth = this.makeDate(this.maximum.getUTCFullYear(), this.maximum.getUTCMonth() + 1, 1);
      this.prev.disabled = firstVisible <= minimumMonth;
      this.next.disabled = firstVisible >= maximumMonth;
    }

    renderSlots() {
      const values = this.slotValues(this.selected);
      const name = 'fm-delivery-slot-' + (this.dataset.context || 'page');
      this.slotOptions.innerHTML = values.map((value) => {
        const checked = value === this.selectedSlot;
        return '<label class="fm-slot"><input type="radio" name="' + name +
          '" value="' + this.escapeHtml(value) + '"' + (checked ? ' checked' : '') +
          '><span>' + this.escapeHtml(value) + '</span></label>';
      }).join('');

      this.slotOptions.querySelectorAll('input').forEach((input) => {
        input.addEventListener('change', () => this.selectSlot(input.value));
      });
    }

    selectDate(value) {
      this.selected = this.parseDate(value);
      if (!this.slotValues(this.selected).includes(this.selectedSlot)) this.selectedSlot = '';
      this.dateError.hidden = true;
      this.slotError.hidden = true;
      this.updateInputs();
      this.render();
      this.broadcast();
      this.saveAttributes();
    }

    selectSlot(value) {
      this.selectedSlot = value;
      this.slotError.hidden = true;
      this.updateInputs();
      this.broadcast();
      this.saveAttributes();
    }

    updateInputs() {
      this.dateInput.value = this.formatDate(this.selected);
      this.slotInput.value = this.selectedSlot;
    }

    broadcast() {
      document.dispatchEvent(new CustomEvent('fm:delivery-updated', {
        detail: {
          source: this,
          date: this.dateInput.value,
          slot: this.slotInput.value,
          instructions: this.instructions.value
        }
      }));
    }

    validateCheckout(event) {
      const missingDate = !this.dateInput.value;
      const missingSlot = !this.slotInput.value;
      this.dateError.hidden = !missingDate;
      this.slotError.hidden = !missingSlot;

      if (!missingDate && !missingSlot) return true;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    saveAttributes() {
      this.status.classList.remove('is-saved');
      this.status.textContent = this.dataset.saving;

      fetch((window.routes && window.routes.cart_update_url) || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: {
            'Fecha de entrega': this.dateInput.value,
            'Franja de entrega': this.slotInput.value,
            'Indicaciones de entrega': this.instructions.value.trim()
          }
        })
      })
        .then(function(response) {
          if (!response.ok) throw new Error('Cart update failed');
          return response.json();
        })
        .then(() => {
          this.status.textContent = this.dateInput.value && this.slotInput.value ? this.dataset.saved : '';
          this.status.classList.toggle('is-saved', Boolean(this.status.textContent));
        })
        .catch(() => {
          this.status.textContent = '';
          this.status.classList.remove('is-saved');
        });
    }

    escapeHtml(value) {
      const span = document.createElement('span');
      span.textContent = value || '';
      return span.innerHTML;
    }
  });
}